import { z } from 'zod';

import type { App, CreateAppInput } from '@keboola/api-client/developerPortal/types';

import type { ComponentTypeOrOther } from './vocabulary';

/**
 * A component id, as apps-api validates it at creation. Immutable afterwards.
 *
 * - The trailing dash gets its own message: it is the common mistake.
 */
export const appIdSchema = z
  .string()
  .min(3)
  .max(64)
  .refine((id) => !id.endsWith('-'), { message: 'Must not end with a dash' })
  .refine((id) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id), {
    message: 'Only lowercase letters, digits and single dashes between them',
  });

/**
 * Type → id prefix.
 *
 * - `transformation` and `other` get no prefix.
 * - `satisfies` keeps `keyof typeof` to these six keys.
 */
const ID_PREFIX_BY_TYPE = {
  extractor: 'ex',
  writer: 'wr',
  application: 'app',
  processor: 'processor',
  'code-pattern': 'code-pattern',
  'data-app': 'data-app',
} satisfies Partial<Record<ComponentTypeOrOther, string>>;

/** Whether a type carries a prefix at all — false for `transformation`, `other` and anything unknown. */
const hasIdPrefix = (type: string): type is keyof typeof ID_PREFIX_BY_TYPE =>
  Object.hasOwn(ID_PREFIX_BY_TYPE, type);

/**
 * Slugifies a component name into something `appIdSchema` accepts.
 *
 * - Stray characters go *before* dashes collapse: `tutorial-(joe)` → `tutorial-joe`.
 * - `[^a-z0-9-]`, not `[^\w-]`: `appIdSchema` rejects underscores.
 */
const webalize = (text: string): string =>
  text
    .normalize('NFKD')
    .toLowerCase()
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^a-z0-9-]+/g, '')
    .replaceAll(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

/**
 * The auto-filled component id, re-derived as the user types the name or picks a type.
 *
 * - `''` when either input is missing or the name slugifies to nothing.
 * - An unrecognised type, and `other`, gets no prefix.
 * - A bare slug: apps-api prefixes the vendor (`acme.ex-gmail`); the form shows `acme.` as an addon.
 */
export const createWebalizedAppId = (name?: string, type?: string): string => {
  if (name === undefined || name === '' || type === undefined || type === '') return '';

  // A bare `ex-` would fail `appIdSchema` (trailing dash), so the field stays empty instead.
  const slug = webalize(name);
  if (slug === '') return '';

  return hasIdPrefix(type) ? `${ID_PREFIX_BY_TYPE[type]}-${slug}` : slug;
};

/** The three fields the create form actually collects. */
export type NewAppInput = {
  name: string;
  type: ComponentTypeOrOther;
  id: string;
};

/**
 * Types that get the Docker UI defaults on creation.
 *
 * ⚠️ No default appears on the form, and skipping them yields components that cannot run:
 * no `repository.type` to deploy to, no `uiOptions` for a configuration UI.
 */
const DOCKER_UI_TYPES: readonly ComponentTypeOrOther[] = [
  'application',
  'extractor',
  'writer',
  'transformation',
];

/**
 * The create payload as apps-api actually accepts it.
 *
 * ⚠️ Wider than `CreateAppInput` on purpose: the spec closes `POST /vendors/{vendor}/apps` at six
 * fields, but the service honours these defaults on creation. The spec in keboola/developer-portal
 * (`docs/openapi.yaml`) is wrong.
 */
export type CreateAppPayload = CreateAppInput &
  Partial<
    Pick<App, 'uiOptions' | 'repository' | 'defaultBucket' | 'defaultBucketStage' | 'actions'>
  >;

export const setDefaultValuesOnCreation = (app: NewAppInput): CreateAppPayload => {
  let values: CreateAppPayload = { ...app };

  if (DOCKER_UI_TYPES.includes(app.type)) {
    values = {
      ...values,
      uiOptions: ['genericDockerUI'],
      repository: { ...values.repository, type: 'ecr' },
    };
  }

  if (app.type === 'extractor') {
    values = { ...values, defaultBucket: true, defaultBucketStage: 'in' };
  }

  if (app.type === 'code-pattern') {
    values = { ...values, actions: ['generate'] };
  }

  return values;
};
